// What a failed provider READ (menu, discovery search, store detail) means
// for the page that asked.
//
// DoorDash limits the whole shared MealOps account and says for how long. Our
// API answers that with a coded 429 (PROVIDER_RATE_LIMITED, retryAfterSeconds,
// plain words). An older API passed Phantom's own text through as a 500 —
// "PROVIDER_RATE_LIMITED: … wait and send the same request again.
// [retry_after_seconds=N]", or dd-cli's "TOOL_RATE_LIMITED: Too many requests
// for user. Retry after N seconds" — and the pages printed it to coaches and
// guests (2026-10-07). Either way the page must say it in plain words, keep
// what it already shows, and try again by itself once the window is over —
// never earlier, since every call into a closed window lengthens it.
import {
  PROVIDER_RATE_LIMITED_CODE,
  providerRetryAfterSeconds,
  providerRetryWaitMs,
} from '@/domains/orders/providerBusyRetry';

const CODED_TEXT = new RegExp(`(^|:\\s*)${PROVIDER_RATE_LIMITED_CODE}:|\\bTOOL_RATE_LIMITED\\b`);

/**
 * Seconds the provider asked a read to stay away, or null when `error` (an
 * API fetch error: { message, status, payload }) is not the provider's rate
 * limit. Our own per-user throttle (code RATE_LIMITED) is not one.
 */
export function providerReadRetryAfterSeconds(error) {
  const payload = error?.payload && typeof error.payload === 'object' ? error.payload : null;
  const coded = providerRetryAfterSeconds(payload) ?? providerRetryAfterSeconds(error);
  if (coded != null) return coded;
  const text = [error?.message, payload?.error, payload?.message].filter((part) => typeof part === 'string').join(' ');
  if (!CODED_TEXT.test(text)) return null;
  return providerRetryAfterSeconds({ code: PROVIDER_RATE_LIMITED_CODE, message: text });
}

/** How long a read waits before asking again: the window plus a pad, never less. */
export const providerReadRetryWaitMs = (retryAfterSeconds) => providerRetryWaitMs(retryAfterSeconds);

// All automatic re-reads of one page load together; past it the page offers
// Try again instead. DoorDash windows have run from seconds to ~5 minutes.
export const PROVIDER_READ_WAIT_BUDGET_MS = 10 * 60_000;

/** "in about 45 seconds" / "in about 3 minutes", for coach-facing copy. */
export const describeWait = (ms) => {
  const seconds = Math.max(1, Math.ceil(Number(ms) / 1000));
  return seconds < 90 ? `in about ${Math.ceil(seconds / 5) * 5} seconds` : `in about ${Math.ceil(seconds / 60)} minutes`;
};

// A coach in the DoorDash pilot whose DoorDash sign-in is missing or expired.
// Every DoorDash read runs on their own account, so none can run until they
// connect: the page asks them to connect, never to "try again in a minute".
export const DOORDASH_CONNECT_CODE = 'doordash_reconnect_required';

export function needsDoordashConnect(error) {
  const payload = error?.payload && typeof error.payload === 'object' ? error.payload : null;
  return (payload?.code ?? error?.code) === DOORDASH_CONNECT_CODE;
}
